import {
  addMonths,
  endOfMonth,
  startOfMonth,
  format,
  lastDayOfWeek,
  parse,
  startOfWeek,
  isLeapYear,
  isToday,
  isYesterday,
} from 'date-fns';
import { MixedDate } from './types';

const firstDayOfTheMonth = 1;
const firstMonthOfTheYear = 0;
const lastMonthOfTheYear = 11;
const lastDayOfTheYear = 31;
const monthsInQuarter = 2;

/**
 * Match strings starting with ISO 8601 Date pattern yyyy-mm-dd or YearMonths (yyyy-mm)
 * For now we don't handle hours, minutes or seconds.
 */
const reDatePattern = /^(\d{4})-(\d{2})(-(\d{2}))?/;

/**
 * Poor's man date validation.
 */
function isValidDate(year: number, month: number, day: number): boolean {
  if (month < firstMonthOfTheYear || month > lastMonthOfTheYear) {
    return false;
  }

  return day >= firstDayOfTheMonth;
}

/**
 * Parses a string date with format yyyy-mm-dd or yyyy-mm into a native Date object.
 * Returns an invalid date if the given date string is invalid.
 */
export function toDate(date: MixedDate): Date {
  if (date instanceof Date) {
    return new Date(date);
  }

  const dateParts: Array<string> | null | undefined = date.match(reDatePattern);

  if (dateParts) {
    const year = parseInt(dateParts[1], 10);
    const month = parseInt(dateParts[2], 10);
    const day = dateParts[4] ? parseInt(dateParts[4], 10) : firstDayOfTheMonth;

    if (!isValidDate(year, month - 1, day)) {
      throw new Error(`Couldn't parse date with invalid day or month: ${date}`);
    }

    return new Date(year, month - 1, day);
  }

  throw new Error(`Could not parse date with invalid pattern: ${date}`);
}

/**
 * Accepts a date object and formatting options and returns a formatted date string.
 *
 * For all available locales and format options see:
 * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString#Syntax}
 */
type FormatDateOptions = Intl.DateTimeFormatOptions & {
  locales?: Array<string> | string;
};

export function formatDate(date: Date, options?: FormatDateOptions): string {
  const emptyOptions: FormatDateOptions = {};
  const { locales, ...rest } = options || emptyOptions;
  return date.toLocaleDateString(locales || 'en-US', rest);
}

/**
 * Accepts a date object and returns an ISO date string in the format yyyy-mm-dd.
 */
export function getISODateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Accepts a date object, converts the date to utc and returns an ISO date string in the format yyyy-mm-dd.
 */
export function getISODateStringInUTC(date: Date): string {
  const dateUTC = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return getISODateString(dateUTC);
}

/**
 * Get the number of calendar months between a later and earlier date.
 */
export function differenceInCalendarMonths(laterDate: Date, earlierDate: Date): number {
  const yearDifference = laterDate.getFullYear() - earlierDate.getFullYear();
  const monthDifference = laterDate.getMonth() - earlierDate.getMonth();
  return yearDifference * 12 + monthDifference;
}

/**
 * Get a date set to the last day of the month for the given date.
 */
export function getLastDateOfMonth(date: MixedDate): Date {
  const initialDate = toDate(date);
  const month = initialDate.getMonth();
  const year = initialDate.getFullYear();
  return new Date(year, month + 1, 0);
}

/**
 * Get a date set to the last day of the month before the month of the given date. This is useful in
 * situations where the date at the end of the previous month period is needed.
 */
export function getLastDateOfPreviousMonth(date: MixedDate): string {
  return format(endOfMonth(addMonths(parse(date), -1)), 'YYYY-MM-DD');
}

/**
 * Gets the first day of the month for the date provided
 */
export function getStartOfMonth(date: MixedDate): Date {
  return startOfMonth(date);
}

/**
 * Get a date set to the first day of the year for the given date.
 */
function getFirstDateOfYear(date: Date): Date {
  return new Date(date.getFullYear(), firstMonthOfTheYear, firstDayOfTheMonth);
}

/**
 * Get a date set to the last day of the year for the given date.
 */
function getLastDateOfYear(date: Date): Date {
  return new Date(date.getFullYear(), lastMonthOfTheYear, lastDayOfTheYear);
}

/**
 * Get the array of first day and last day of the year for the given date.
 */
function getFirstAndLastDateInYear(date: Date): [Date, Date] {
  return [getFirstDateOfYear(date), getLastDateOfYear(date)];
}

/**
 * Get the array of first day and last day of the week for the given date.
 */
function getFirstAndLastDateInWeek(date: Date): [Date, Date] {
  const options = {
    weekStartsOn: 1,
  };
  return [startOfWeek(date, options), lastDayOfWeek(date, options)];
}

/**
 * Returns true if the given date is the first day of the month, false otherwise.
 */
function isFirstDayOfMonth(date: Date): boolean {
  return date.getDate() === firstDayOfTheMonth;
}

/**
 * Returns true if the given date is the last day of the month, false otherwise.
 */
function isLastDayOfMonth(date: Date): boolean {
  return date.getDate() === getLastDateOfMonth(date).getDate();
}

/**
 * Returns true if the given date is the first month of a quarter, false otherwise.
 * Valid month indexes: 0, 3, 6, 9
 */
function isFirstMonthOfQuarter(date: Date): boolean {
  return date.getMonth() <= 9 && date.getMonth() % 3 === 0;
}

/**
 * Returns true if the given date is the first day of the year, false otherwise.
 */
function isFirstMonthOfYear(date: Date): boolean {
  return date.getMonth() === firstMonthOfTheYear;
}

/**
 * Returns true if the given date is the last month of the year, false otherwise.
 */
function isLastMonthOfYear(date: Date): boolean {
  return date.getMonth() === lastMonthOfTheYear;
}

/**
 * Returns true if the given start and end dates have the same month, false otherwise.
 */
function isSameMonth(startDate: Date, endDate: Date): boolean {
  return startDate.getMonth() === endDate.getMonth();
}

/**
 * Returns true if the given start and end dates have the same year, false otherwise.
 */
function isSameYear(startDate: Date, endDate: Date): boolean {
  return startDate.getFullYear() === endDate.getFullYear();
}

/**
 * Takes the incoming date and returns a date age (e.g. "today")
 * or the display-friendly date if age not available.
 */
export function getDisplayDate(date: Date | string) {
  if (isToday(date)) return 'today';
  if (isYesterday(date)) return 'yesterday';
  return format(date, 'MMM DD, YYYY');
}

/**
 * Takes a date and returns an abbreviated string format representing the first
 * and last day of the week containing it
 */
export function getWeeklyIntervalString(date: Date): string {
  const [startDate, endDate] = getFirstAndLastDateInWeek(date);
  const formatOptions: Partial<FormatDateOptions> = {
    month: 'short',
    day: 'numeric',
  };
  const startDateString = formatDate(startDate, formatOptions);

  if (startDate.getMonth() === endDate.getMonth()) {
    return `${startDateString} – ${endDate.getDate()}`;
  }

  const endDateString = formatDate(endDate, formatOptions);
  return `${startDateString} – ${endDateString}`;
}

/**
 * Returns true if the given dates span over exactly one month, false otherwise.
 */
export function isRangeMonth(startDate: MixedDate, endDate: MixedDate): boolean {
  const start = toDate(startDate);
  const end = toDate(endDate);
  return (
    isFirstDayOfMonth(start) &&
    isLastDayOfMonth(end) &&
    isSameYear(start, end) &&
    isSameMonth(start, end)
  );
}

/**
 * Returns true if the given dates span over exactly one quarter, false otherwise.
 */
export function isRangeQuarter(startDate: MixedDate, endDate: MixedDate): boolean {
  const start = toDate(startDate);
  const end = toDate(endDate);
  return (
    isFirstDayOfMonth(start) &&
    isLastDayOfMonth(end) &&
    isSameYear(start, end) &&
    isFirstMonthOfQuarter(start) &&
    differenceInCalendarMonths(end, start) === monthsInQuarter
  );
}

/**
 * Returns true if the given dates span over exactly one year, false otherwise.
 */
export function isRangeYear(startDate: MixedDate, endDate: MixedDate): boolean {
  const start = toDate(startDate);
  const end = toDate(endDate);
  return (
    isFirstDayOfMonth(start) &&
    isFirstMonthOfYear(start) &&
    isLastDayOfMonth(end) &&
    isLastMonthOfYear(end) &&
    isSameYear(start, end)
  );
}

/**
 * Returns true if the given dates span over exactly one month, one quarter or
 * one year, false otherwise.
 */
export function isRangeMonthQuarterOrYear(startDate: MixedDate, endDate: MixedDate): boolean {
  const start = toDate(startDate);
  const end = toDate(endDate);
  return isRangeMonth(start, end) || isRangeQuarter(start, end) || isRangeYear(start, end);
}

/**
 * Get the first and last date for the given calendar period. If the given
 * period is not a month, quarter or year, the function returns the first
 * and last dates of the year found in the end date of the given period.
 */
export function getFirstAndLastDatesInCalendarPeriod(
  startDate: MixedDate,
  endDate: MixedDate,
): [Date, Date] {
  if (isRangeMonthQuarterOrYear(startDate, endDate)) {
    return [toDate(startDate), toDate(endDate)];
  }

  return getFirstAndLastDateInYear(toDate(endDate));
}

/**
 * Gets the number of the quarter the given date is contained in.
 */
export function getQuarter(date: MixedDate): number {
  return Math.ceil((toDate(date).getMonth() + 1) / 3);
}

/**
 * Format a fiscal period for the given start and end dates.
 *
 * If the dates passed do not correspond to a fiscal period (i.e. month, quarter
 * or year), the component will render the start and end date separated by a
 * delimiter character.
 */
export function formatDatePeriod(
  startDate: MixedDate,
  endDate: MixedDate,
  delimiter = ' – ',
): string {
  const start = toDate(startDate);
  const end = toDate(endDate);

  if (isRangeMonth(start, end)) {
    return formatDate(start, {
      year: 'numeric',
      month: 'short',
    });
  }

  if (isRangeQuarter(start, end)) {
    return `Q${getQuarter(start)} ${start.getFullYear()}`;
  }

  if (isRangeYear(start, end)) {
    return `${start.getFullYear()}`;
  }

  const customRangeFmtOptions: FormatDateOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const formattedStartDate = formatDate(start, customRangeFmtOptions);
  const formattedEndDate = formatDate(end, customRangeFmtOptions);
  return `${formattedStartDate}${delimiter}${formattedEndDate}`;
}

/**
 * Returns the offset in terms of "months earlier" than a standard fiscal year ending December 31.
 * For example, if a fiscal year ends on April 30, returns 8 because it is 8 months earlier than a
 * standard fiscal year that matches a calendar year. For the standard fiscal year ending December
 * 31, returns 0.
 */
export function getFiscalYearOffset(fiscalYearEnd: MixedDate): number {
  let date;

  if (typeof fiscalYearEnd === 'string') {
    // Convert a MM-DD month-day to a date to be able to extract its month
    date = toDate(`2000-${fiscalYearEnd}`);
  }

  if (fiscalYearEnd instanceof Date) {
    date = fiscalYearEnd;
  }

  if (!date) {
    return 0;
  }

  return 11 - date.getMonth();
}

/**
 * Returns the fiscal year of a given YearMonth, which might be the following year in the case that
 * the the fiscal year for the calendar year that month is in has already ended. For example, for
 * the month May, 2018 and a fiscal year ending April 30 (which would have an offset of 8) it would
 * return 2019 because May, 2018 is after the end of the fiscal year that ends April 30, 2018.
 */
export function getFiscalYearForMonth(yearMonth: MixedDate, fiscalYearOffset: number): number {
  const date = toDate(yearMonth);
  const fiscalYearDate = new Date(
    Date.UTC(date.getFullYear(), date.getMonth() + fiscalYearOffset, date.getDate()),
  );
  return fiscalYearDate.getUTCFullYear();
}

/**
 * Returns a date representing the last day of the requested fiscal year.
 */
export function getFiscalYearEndDate(fiscalYearEnd: string, year: number): Date {
  const leapAdjustedFiscalYearEnd =
    isLeapYear(toDate(`${year}-01`)) && fiscalYearEnd === '02-28' ? '02-29' : fiscalYearEnd;
  return toDate(`${year}-${leapAdjustedFiscalYearEnd}`);
}

/**
 * Returns the date at the beginning of the fiscal year that ends on the given date.
 */
export function getFiscalYearStartDate(fiscalYearEndDate: Date): Date {
  return new Date(
    fiscalYearEndDate.getFullYear() - 1,
    fiscalYearEndDate.getMonth(),
    fiscalYearEndDate.getDate() + 1,
  );
}

/**
 * Returns the twelve hour time (eg. 12:06 PM).
 */
export function getTwelveHourTime(date: MixedDate): string {
  const newDate = new Date(date);
  return newDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
  });
}

/**
 * Returns the true if java local date (1987-04-01) is in year month (1987-04)
 */
export function isLocalDateInYearMonth(date: string, yearMonth: string): boolean {
  return date.startsWith(yearMonth);
}
